import React, {useState, useEffect, useRef, forwardRef, useImperativeHandle} from 'react';
import {TextField, Paper, Box} from '@mui/material';

const BarcodeInput = forwardRef(({
    onScan,
    disabled = false,
    placeholder = "Отсканируйте штрихкод или введите вручную",
    label = "Штрихкод",
    autoFocus = true,
    style = {}
}, ref) => {
    const [barcode, setBarcode] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const inputRef = useRef(null);
    const scanTimeoutRef = useRef(null);
    const inputBufferRef = useRef([]);

    // Предоставляем методы для parent компонента
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current && !disabled) {
                inputRef.current.focus();
            }
        }
    }), [disabled]);

    useEffect(() => {
        if (autoFocus && inputRef.current && !disabled) {
            inputRef.current.focus();
        }
    }, [autoFocus, disabled]);

    // Обработка сканирования штрихкодов
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Игнорируем если поле отключено или фокус не на нашем поле
            if (disabled || document.activeElement !== inputRef.current) {
                return;
            }

            // Специальные клавиши, которые не учитываем при сканировании
            const ignoredKeys = ['Shift', 'Control', 'Alt', 'Meta', 'Tab', 'Escape'];

            if (ignoredKeys.includes(event.key)) {
                return;
            }

            // Если нажат Enter - обрабатываем как конец сканирования
            if (event.key === 'Enter') {
                event.preventDefault();
                handleScanComplete();
                return;
            }

            // Добавляем символ в буфер для сканирования
            if (event.key.length === 1) { // Только обычные символы
                inputBufferRef.current.push(event.key);
                setIsScanning(true);

                // Сбрасываем таймаут - если сканер быстро вводит, продолжаем накапливать
                if (scanTimeoutRef.current) {
                    clearTimeout(scanTimeoutRef.current);
                }

                // Если через 100мс не было новых символов - заканчиваем сканирование
                scanTimeoutRef.current = setTimeout(() => {
                    handleScanComplete();
                }, 100);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }
        };
    }, [disabled]);

    const handleScanComplete = () => {
        const scannedBarcode = inputBufferRef.current.join('');

        if (scannedBarcode && scannedBarcode.length > 0) {
            setBarcode(scannedBarcode);
            if (onScan) {
                onScan(scannedBarcode);
            }
        }

        // Очищаем буфер и состояние
        inputBufferRef.current = [];
        setIsScanning(false);

        // Очищаем поле для следующего сканирования
        setTimeout(() => {
            setBarcode('');
            if (inputRef.current && autoFocus) {
                inputRef.current.focus();
            }
        }, 100);
    };

    const handleInputChange = (event) => {
        const value = event.target.value;
        setBarcode(value);

        // Очищаем буфер сканирования при ручном вводе
        inputBufferRef.current = [];
        setIsScanning(false);
    };

    const handleInputKeyPress = (event) => {
        if (event.key === 'Enter' && barcode) {
            event.preventDefault();
            if (onScan) {
                onScan(barcode);
            }
            setBarcode('');
        }
    };

    const handleInputFocus = () => {
        // При фокусе на поле очищаем буфер сканирования
        inputBufferRef.current = [];
        setIsScanning(false);
    };

    const handleInputBlur = () => {
        // При потере фокуса завершаем сканирование если оно было активно
        if (isScanning) {
            handleScanComplete();
        }
    };

    return (
        <Box style={style}>
            <TextField
                ref={inputRef}
                label={label}
                placeholder={placeholder}
                value={barcode}
                onChange={handleInputChange}
                onKeyPress={handleInputKeyPress}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={disabled}
                fullWidth
                variant="outlined"
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                            borderColor: '#f24405',
                            borderWidth: '2px'
                        }
                    },
                    '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                            color: '#f24405'
                        }
                    }
                }}
                InputProps={{
                    style: {
                        backgroundColor: isScanning ? '#e8f5e8' : '#ffffff',
                        border: isScanning ? '2px solid #4caf50' : undefined
                    }
                }}
            />

            {isScanning && (
                <Paper
                    elevation={2}
                    style={{
                        padding: '8px 12px',
                        marginTop: '4px',
                        backgroundColor: '#e8f5e8',
                        border: '1px solid #4caf50'
                    }}
                >
                    <div style={{ fontSize: '12px', color: '#2e7d32' }}>
                        🔍 Сканирование... ({inputBufferRef.current.join('')})
                    </div>
                </Paper>
            )}

            {!isScanning && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Наведите сканер на штрихкод или введите код вручную и нажмите Enter
                </div>
            )}
        </Box>
    );
});

BarcodeInput.displayName = 'BarcodeInput';

export default BarcodeInput;